<template>
    <div class="set-banner-status-dropdown">
        <Dropdown
            placement="bottom-start"
            transfer
            trigger="custom"
            :visible="dropdownIsVisible"
            @onClickoutside="toggleDropdown"
        >
            <SquareButton
                :is-active="dropdownIsVisible"
                :is-loading="isSettingDeliverablesStatus"
                @click="toggleDropdown"
            >
                <div>
                    <QaStatusDot :status="status" />
                    <Icon :class="arrowClasses" type="ios-arrow-down" />
                </div>
            </SquareButton>

            <template #list>
                <QaStatusDropdownItems :selected-status="status" @statusSelected="setDeliverableStatus" />
            </template>
        </Dropdown>
    </div>
</template>

<script lang="ts">
import QaStatusDropdownItems from "@/components/Campaign/QaStatusDropdownItems.vue";
import SquareButton from "@/components/common/SquareButton.vue";
import QaStatusDot from "@/components/Qa/QaStatusDot.vue";
import { BannerQaStatus, BannerQaStatusLabel } from "@/enums/banners";
import { setDeliverablesGlobalStatus } from "@/services/Qa";
import { DeliverableLibraryAction, DeliverableLibraryGetters } from "@/store/modules/deliverableLibrary";
import type { ConstructedDeliverable } from "@/types/types";
import type { PropType } from "vue";

export default {
    components: {
        QaStatusDot,
        QaStatusDropdownItems,
        SquareButton
    },
    props: {
        deliverable: {
            required: true,
            type: Object as PropType<ConstructedDeliverable>
        }
    },

    emits: ["statusHasBeenUpdated"],

    data() {
        return {
            dropdownIsVisible: false,
            isSettingDeliverablesStatus: false
        };
    },
    computed: {
        arrowClasses() {
            const classes = ["set-banner-status-dropdown__select-arrow"];
            if (this.dropdownIsVisible) {
                classes.push("set-banner-status-dropdown__select-arrow--active ");
            }
            return classes;
        },
        campaignId() {
            return this.$route.params.campaignId;
        },
        status() {
            return this.$store.getters[DeliverableLibraryGetters.computedDeliverableStatus](this.deliverable.idHash);
        }
    },
    methods: {
        toggleDropdown() {
            this.dropdownIsVisible = !this.dropdownIsVisible;
        },
        async setDeliverableStatus(status: (typeof BannerQaStatus)[keyof typeof BannerQaStatus]) {
            this.toggleDropdown();
            this.isSettingDeliverablesStatus = true;

            try {
                const deliverables = await setDeliverablesGlobalStatus(this.campaignId, [this.deliverable], status);
                this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, deliverables);

                this.$snackbar.success(`The deliverable has had its status set to "${BannerQaStatusLabel[status]}"`);
                this.$emit("statusHasBeenUpdated");
            } catch {
                this.$snackbar.error(
                    "There was an unexpected error and the deliverable status was unable to be set. Please try again in a few moments."
                );
            }

            this.isSettingDeliverablesStatus = false;
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;

.set-banner-status-dropdown {
    display: inline-block;
}

.set-banner-status-dropdown__select-arrow {
    font-size: $font-size-small;
    margin: 0 0 0 2px;
    transition: transform 0.2s linear;
}

.set-banner-status-dropdown__select-arrow--active {
    transform: rotate(180deg);
}
</style>
